import { observable } from "mobx";
import { useEffect, useState } from "react";

import { useCartItems } from "@faire/retailer/hooks/cart/useCartItems";
import { useCartUpdate } from "@faire/retailer/hooks/cart/useCartUpdate";
import { UpdateCartHandler } from "@faire/retailer/stores/domain/CartStore/consts";

import { IProductAddOptions } from "./ProductAddOptions";
import { getUpdateCartFunction } from "./utils";

/**
 * Satisfies the IProductAddOptions interface by combining
 * useCartItems and useCartUpdate.
 */
export const useCartProductAddOptions = (
  updateItemOverride?: UpdateCartHandler
): IProductAddOptions => {
  const {
    cartItems,
    getItemByToken,
    getProductTileQuantityInCart,
    getItemByProductOptionTokenAndCustomizations,
  } = useCartItems();
  const { updateCartItemsWithUpdateHandler } = useCartUpdate();

  return {
    addedItems: cartItems,
    getAddedItemByToken: getItemByToken,
    getAddedItemQuantityByToken: getProductTileQuantityInCart,
    getAddedItemByProductOptionTokenAndCustomizations:
      getItemByProductOptionTokenAndCustomizations,
    addOrUpdateItems: () => {},
    addOrUpdateItemsV2: getUpdateCartFunction(
      updateItemOverride ?? updateCartItemsWithUpdateHandler,
      true
    ),
    addOrUpdateItemsV2withSyncInventoryFetch: getUpdateCartFunction(
      updateItemOverride ?? updateCartItemsWithUpdateHandler,
      false
    ),
  };
};

/**
 * @deprecated Use `useCartProductAddOptions` instead.
 *
 * This hooks creates an observable `ProductAddOptions` object
 * that's used to inject into ProductAddToCartState.
 * We can get rid of this hook once `ProductAddToCartState`
 * has been migrated to hooks.
 */
export const useCartProductAddOptionsObservable = (
  updateItemOverride?: UpdateCartHandler
): IProductAddOptions => {
  const productAddOptions = useCartProductAddOptions(updateItemOverride);
  const [productAddOptionsObservable] = useState(() =>
    observable(productAddOptions)
  );

  /**
   * Mobx isn't reactive and needs to be explicitly updated whenever
   * reactive properties change.
   */
  useEffect(() => {
    productAddOptionsObservable.addedItems = productAddOptions.addedItems;
  }, [productAddOptions.addedItems, productAddOptionsObservable]);
  useEffect(() => {
    productAddOptionsObservable.getAddedItemByToken =
      productAddOptions.getAddedItemByToken;
  }, [productAddOptions.getAddedItemByToken, productAddOptionsObservable]);
  useEffect(() => {
    productAddOptionsObservable.getAddedItemQuantityByToken =
      productAddOptions.getAddedItemQuantityByToken;
  }, [
    productAddOptions.getAddedItemQuantityByToken,
    productAddOptionsObservable,
  ]);
  useEffect(() => {
    productAddOptionsObservable.getAddedItemByProductOptionTokenAndCustomizations =
      productAddOptions.getAddedItemByProductOptionTokenAndCustomizations;
  }, [
    productAddOptions.getAddedItemByProductOptionTokenAndCustomizations,
    productAddOptionsObservable,
  ]);

  useEffect(() => {
    productAddOptionsObservable.addOrUpdateItemsV2 =
      productAddOptions.addOrUpdateItemsV2;
  }, [productAddOptions.addOrUpdateItemsV2, productAddOptionsObservable]);
  useEffect(() => {
    productAddOptionsObservable.addOrUpdateItemsV2withSyncInventoryFetch =
      productAddOptions.addOrUpdateItemsV2withSyncInventoryFetch;
  }, [
    productAddOptions.addOrUpdateItemsV2withSyncInventoryFetch,
    productAddOptionsObservable,
  ]);

  return productAddOptionsObservable;
};
